import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle } from 'lucide-react';

/* ===========================================================================
 * THE ONE FOOTER — who built this, and how to reach them when it breaks.
 *
 * It exists because the only place the vendor was named was the sign-in
 * screen: the person who needs support is, by definition, already signed in
 * and looking at whatever went wrong, and there was nothing on that screen
 * telling them who to ask. A support address nobody can find is not a support
 * address.
 *
 * WHY THE NUMBER IS NOT PRINTED. `wa.me` opens the conversation on whatever
 * device the reader is holding — that is the whole affordance, and a raw
 * +9715… beside it invites somebody to copy fifteen digits by hand into a
 * different app and mistype one. The link IS the number; the visible label is
 * what it does.
 *
 * WHERE IT SITS. Inside the shell's reading column, after the outlet, so it
 * takes the same gutter as the page above it and scrolls away rather than
 * standing between the reader and the content. `AppShell` keeps it off the
 * WIDE routes — the full-bleed boards, whose container is `h-full` and whose
 * child owns the whole viewport; appending a block to that column makes the
 * board shorter than its own scrollport and gives a map a scrollbar. Somebody
 * on the map who needs support is one navigation away from a screen with it.
 *
 * `rel="noreferrer"` on both external links, and no `target` on the two
 * protocol links: a `mailto:` or `https://wa.me` handler opens its own app,
 * and forcing a tab as well leaves an empty one behind.
 * ======================================================================== */

/** Where support actually lands. One definition, so the three links agree. */
const VENDOR_URL = 'https://araneae.net';
const SUPPORT_EMAIL = 'it@araneae.net';
/** Digits only, no `+` and no spaces — the shape `wa.me` requires. */
const SUPPORT_WHATSAPP = '971557997697';

export function AppFooter(): JSX.Element {
  const { t } = useTranslation();
  const link =
    'inline-flex items-center gap-1.5 rounded-xs underline decoration-border underline-offset-2 ' +
    'transition-colors duration-fast ease-standard hover:text-foreground hover:decoration-current ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

  return (
    <footer className="mt-section flex flex-wrap items-center gap-x-component gap-y-tight border-t border-border pt-component text-copy-sm text-muted-foreground">
      <span>
        {t('common.developedByPrefix', 'Developed by')}{' '}
        <a className={link} href={VENDOR_URL} target="_blank" rel="noreferrer">
          araneae.net
        </a>
      </span>
      {/* The trailing pair is the support cluster — `ms-auto` puts it on the
          opposite edge on a desktop and lets it wrap under on a phone. */}
      <span className="ms-auto flex flex-wrap items-center gap-x-component gap-y-tight">
        <a className={link} href={`mailto:${SUPPORT_EMAIL}`} rel="noreferrer">
          <Mail size={14} aria-hidden />
          {SUPPORT_EMAIL}
        </a>
        <a
          className={link}
          href={`https://wa.me/${SUPPORT_WHATSAPP}`}
          target="_blank"
          rel="noreferrer"
        >
          <MessageCircle size={14} aria-hidden />
          {t('common.whatsappSupport', 'WhatsApp support')}
        </a>
      </span>
    </footer>
  );
}
